import { AxiosResponse } from 'axios';

import client from 'api/client';
import {
    ClientServiceProvider,
    OrganizationOnboardingResponseInterface,
} from 'api/models/response/ClientServiceProvider';
import {
    CompanyAgreementDocumentInterface,
    DocumentInterface,
    DocumentType,
    OrganizationAgentFormInterface,
    OrganizationAgreementFormInterface,
    OrganizationAgreementInterface,
    OrganizationInterface,
    OrganizationOnboardingFormInterface,
} from 'types';
import { convertBlobToBase64 } from 'utils/file';

/** JSON shape of `/agreements/<id>/resource_uri` once the agreement is signed. */
interface CompanyAgreementResourceResponse {
    url?: string;
    resource_name?: string;
}

export const getClientServiceProviders = (
    company_name?: string,
    exclude_own_service_provider: boolean = false
): Promise<AxiosResponse<ClientServiceProvider[]>> => {
    return client.get<ClientServiceProvider[]>('/client-service-providers', {
        params: {
            ...(company_name ? { company_name } : {}),
            ...(exclude_own_service_provider ? { exclude_own_service_provider } : {}),
        },
    });
};

export const onboardClient = (
    payload: Partial<OrganizationOnboardingFormInterface>
): Promise<AxiosResponse<OrganizationOnboardingResponseInterface>> => {
    return client.post('/client-service-providers/onboarding', payload);
};

export const getClientServiceProviderById = (
    id: string
): Promise<AxiosResponse<ClientServiceProvider>> => {
    return client.get<ClientServiceProvider>(`/client-service-providers/${id}`);
};

// document

export const getClientCompanyDocuments = (id: string, document_type?: DocumentType) => {
    return client.get<DocumentInterface[]>(`/client-service-providers/${id}/documents`, {
        params: document_type ? { document_type } : undefined,
    });
};

export const assignDocument = (
    documentTemplateId: number,
    payload: {
        document_template_id: number;
    }
) => {
    return client.post<DocumentInterface[]>(
        `/client-service-providers/${documentTemplateId}/documents`,
        payload
    );
};

export const getDocumentPreviewUrl = (serviceProviderId: string, documentTemplateId: number) => {
    return client.get(
        `/client-service-providers/${serviceProviderId}/documents/${documentTemplateId}/resource_uri`
    );
};

export const getDocumentTemplateUrl = (serviceProviderId: string, documentTemplateId: number) => {
    return client.get(
        `/client-service-providers/${serviceProviderId}/documents/${documentTemplateId}/template_uri`
    );
};

export const getDocumentSampleUrl = (serviceProviderId: string, documentTemplateId: number) => {
    return client.get(
        `/client-service-providers/${serviceProviderId}/documents/${documentTemplateId}/sample_uri`
    );
};

export const uploadDocument = (
    serviceProviderId: string,
    documentId: string | number,
    payload: FormData
) => {
    return client.post(
        `/client-service-providers/${serviceProviderId}/documents/${documentId}/resource`,
        payload,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
        }
    );
};

export const updateDocument = (
    serviceProviderId: string,
    documentId: number,
    payload: FormData
) => {
    return client.patch(
        `/client-service-providers/${serviceProviderId}/documents/${documentId}`,
        payload,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
        }
    );
};

// agents
export const getOrganizationAgents = (serviceProviderId: string) =>
    client.get(`/client-service-providers/${serviceProviderId}/agents`);

export const createOrganizationAgent = (
    serviceProviderId: string,
    payload: OrganizationAgentFormInterface
) => client.post(`/client-service-providers/${serviceProviderId}/agents`, payload);

// organization profile
export const getOrganizationProfile = (
    serviceProviderId: string
): Promise<AxiosResponse<OrganizationInterface>> =>
    client.get<OrganizationInterface>(`/client-service-providers/${serviceProviderId}/lmia`);

export const createOrganizationProfile = (
    serviceProviderId: string,
    payload: OrganizationInterface
): Promise<AxiosResponse<OrganizationInterface>> =>
    client.post(`/client-service-providers/${serviceProviderId}/lmia`, payload);

export const updateOrganizationProfile = (
    serviceProviderId: string,
    payload: OrganizationInterface
): Promise<AxiosResponse<OrganizationInterface>> =>
    client.patch(`/client-service-providers/${serviceProviderId}/lmia`, payload);

// agreements
export const getOrganizationAgreements = (
    serviceProviderId: string
): Promise<AxiosResponse<OrganizationAgreementInterface[]>> =>
    client.get(`/client-service-providers/${serviceProviderId}/agreements`);
export const getOrganizationAgreementByAgreementId = (
    serviceProviderId: string,
    agreementId: string
): Promise<AxiosResponse<OrganizationAgreementInterface>> =>
    client.get(`/client-service-providers/${serviceProviderId}/agreements/${agreementId}`);
export const getOrganizationAgreementResourceUri = async (
    serviceProviderId: string,
    agreementId: string,
    isAgreementSigned?: boolean
): Promise<CompanyAgreementDocumentInterface> => {
    const response = await client.get<Blob | CompanyAgreementResourceResponse>(
        `/client-service-providers/${serviceProviderId}/agreements/${agreementId}/resource_uri`,
        {
            responseType: isAgreementSigned ? 'json' : 'blob',
            headers: isAgreementSigned ? {} : { 'Content-Type': 'binary/octet-stream' },
        }
    );

    if (response.data instanceof Blob) {
        return { url: '', file: await convertBlobToBase64(response.data) };
    }

    return { url: response.data.url || response.data.resource_name || '', file: null };
};
export const createOrganizationAgreement = (
    serviceProviderId: string,
    payload: OrganizationAgreementFormInterface
): Promise<AxiosResponse<OrganizationAgreementInterface>> =>
    client.post(`/client-service-providers/${serviceProviderId}/agreements`, payload);

export const createB2BAgreement = (
    serviceProviderId: string,
    payload: OrganizationAgreementFormInterface
): Promise<AxiosResponse<OrganizationAgreementInterface>> =>
    client.post(`/client-service-providers/${serviceProviderId}/agreements`, {
        ...payload,
        resource_type: 'ORG_TO_ORG_RETAINER_AGREEMENT',
    });

export const signB2BAgreement = (
    serviceProviderId: string,
    agreementId: number,
    payload: {
        party_b_signatory_name: string;
        party_b_signatory_title: string;
        party_b_signature_html: string;
    }
): Promise<AxiosResponse<OrganizationAgreementInterface>> =>
    client.post(
        `/client-service-providers/${serviceProviderId}/agreements/${agreementId}/sign`,
        payload
    );

export const getOrganizationB2BAgreements = (
    serviceProviderId: string
): Promise<AxiosResponse<OrganizationAgreementInterface[]>> =>
    client.get(`/client-service-providers/${serviceProviderId}/agreements?type=b2b`);
